/**
 * Effect の適用。
 *
 * 状態（GameState）と世界（NetworkWorld）を書き換える部分はここで完結させ、
 * 「会話を始める」「音を鳴らす」「章クリア」のような
 * 画面側に依頼する必要があるものは戻り値で返す。
 */

import type { DeviceId, InterferenceSource, NetworkWorld } from '../types/network';
import type { Effect, GameState } from '../types/scenario';
import {
  patchFirewallRule,
  patchIpConfig,
  reorderFirewallRule,
  setDeviceTraffic,
  setDnsRecord,
  setInfected,
  setLinkConnected,
  setPortAdminUp,
  setPowered,
  setQuarantined,
  setWirelessBand,
  setWirelessChannel,
  unlockRoom,
} from '../sim/world';

/** applyEffect の戻り値。呼び出し側（エンジン／UI）が処理すべき依頼をまとめる */
export interface EffectOutcome {
  /** 状態か世界に実際の変化があったか */
  changed: boolean;
  /** 開始すべき会話 */
  dialogueIds: string[];
  /** 鳴らすべき効果音 */
  soundIds: string[];
  /** 新しく手に入った手がかり */
  newClues: string[];
  /** 新しく手に入った知識カード */
  newCards: string[];
  /** 章クリアが宣言されたか */
  chapterCompleted: boolean;
}

export function emptyOutcome(): EffectOutcome {
  return {
    changed: false,
    dialogueIds: [],
    soundIds: [],
    newClues: [],
    newCards: [],
    chapterCompleted: false,
  };
}

export function mergeOutcome(a: EffectOutcome, b: EffectOutcome): EffectOutcome {
  return {
    changed: a.changed || b.changed,
    dialogueIds: [...a.dialogueIds, ...b.dialogueIds],
    soundIds: [...a.soundIds, ...b.soundIds],
    newClues: [...a.newClues, ...b.newClues],
    newCards: [...a.newCards, ...b.newCards],
    chapterCompleted: a.chapterCompleted || b.chapterCompleted,
  };
}

// ---------------------------------------------------------------
// 宛先の確認
// ---------------------------------------------------------------

/**
 * 「宛先が居ない効果」を黙って捨てない。
 *
 * 【なぜ要るか】
 * 管理画面のプレースホルダ（`$device`）が置換されないまま
 * `deviceId: "$device"` として届いていたことがあった。
 * 存在しない機器なので世界は何も変わらないのに、誰も何も言わないため
 * 画面には「変更しました」とだけ出て、章がクリア不能になっていた。
 *
 * ゲームは止めない（プレイ中に落とすほうが害が大きい）。
 * ただし開発中に必ず気づけるよう、ここで声を上げる。
 */
function warnMissing(effectType: string, what: string, id: string): void {
  console.warn(
    `[scenario] ${effectType}: ${what}「${id}」が見つかりません。この効果は何もしません`,
  );
}

/** 対象の機器が居ることを確かめる。居なければ警告して false */
function deviceExists(world: NetworkWorld, deviceId: DeviceId, effectType: string): boolean {
  if (world.devices[deviceId]) return true;
  warnMissing(effectType, '機器', deviceId);
  return false;
}

/** 無線設定を持つ機器であることを確かめる */
function wirelessDeviceExists(
  world: NetworkWorld,
  deviceId: DeviceId,
  effectType: string,
): boolean {
  if (!deviceExists(world, deviceId, effectType)) return false;
  if (world.devices[deviceId].wireless) return true;
  console.warn(`[scenario] ${effectType}: 機器「${deviceId}」は無線設定を持ちません`);
  return false;
}

/** 対象のファイアウォールルールが居ることを確かめる */
function firewallRuleExists(
  world: NetworkWorld,
  deviceId: DeviceId,
  ruleId: string,
  effectType: string,
): boolean {
  if (!deviceExists(world, deviceId, effectType)) return false;
  const rules = world.devices[deviceId].firewallRules;
  if (!rules || rules.length === 0) {
    console.warn(`[scenario] ${effectType}: 機器「${deviceId}」はルールを持ちません`);
    return false;
  }
  if (rules.some((rule) => rule.id === ruleId)) return true;
  warnMissing(effectType, 'ルール', ruleId);
  return false;
}

/** Effect を 1 つ適用する。全バリアントに対応 */
export function applyEffect(
  effect: Effect,
  state: GameState,
  world: NetworkWorld,
): EffectOutcome {
  const outcome = emptyOutcome();

  switch (effect.type) {
    case 'setFlag': {
      const before = state.flags[effect.flag];
      state.flags[effect.flag] = effect.value;
      outcome.changed = before !== effect.value;
      break;
    }

    case 'giveClue': {
      if (!state.clues.includes(effect.clueId)) {
        state.clues.push(effect.clueId);
        outcome.newClues.push(effect.clueId);
        outcome.changed = true;
      }
      break;
    }

    case 'giveKnowledgeCard': {
      if (!state.knowledgeCards.includes(effect.cardId)) {
        state.knowledgeCards.push(effect.cardId);
        outcome.newCards.push(effect.cardId);
        outcome.changed = true;
      }
      break;
    }

    case 'setLinkConnected':
      if (!world.links[effect.linkId]) {
        warnMissing(effect.type, 'ケーブル', effect.linkId);
        break;
      }
      outcome.changed = setLinkConnected(world, effect.linkId, effect.end, effect.connected);
      break;

    case 'setPortAdminUp':
      if (!deviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = setPortAdminUp(world, effect.deviceId, effect.portIndex, effect.adminUp);
      break;

    case 'setPowered':
      if (!deviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = setPowered(world, effect.deviceId, effect.powered);
      break;

    case 'setIpConfig':
      if (!deviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = patchIpConfig(world, effect.deviceId, effect.patch);
      break;

    case 'setQuarantined':
      if (!deviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = setQuarantined(world, effect.deviceId, effect.quarantined);
      break;

    case 'startDialogue':
      outcome.dialogueIds.push(effect.dialogueId);
      break;

    case 'unlockRoom':
      if (!world.rooms[effect.roomId]) {
        warnMissing(effect.type, '部屋', effect.roomId);
        break;
      }
      outcome.changed = unlockRoom(world, effect.roomId);
      break;

    case 'completeChapter':
      outcome.chapterCompleted = true;
      break;

    case 'playSound':
      outcome.soundIds.push(effect.soundId);
      break;

    case 'setDnsRecord':
      if (!deviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = setDnsRecord(world, effect.deviceId, effect.hostname, effect.address);
      break;

    case 'setWirelessChannel':
      if (!wirelessDeviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = setWirelessChannel(world, effect.deviceId, effect.channel);
      break;

    case 'setWirelessBand':
      if (!wirelessDeviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = setWirelessBand(world, effect.deviceId, effect.band);
      break;

    case 'setInterferenceActive':
      outcome.changed = setInterferenceActive(state, world, effect.label, effect.active);
      break;

    case 'setFirewallRule':
      if (!firewallRuleExists(world, effect.deviceId, effect.ruleId, effect.type)) break;
      outcome.changed = patchFirewallRule(world, effect.deviceId, effect.ruleId, effect.patch);
      break;

    case 'reorderFirewallRule':
      if (!firewallRuleExists(world, effect.deviceId, effect.ruleId, effect.type)) break;
      outcome.changed = reorderFirewallRule(world, effect.deviceId, effect.ruleId, effect.order);
      break;

    // 章 6。通信量を変えるとリンク使用率が動き、ping の RTT にも効く
    case 'setDeviceTraffic':
      if (!deviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = setDeviceTraffic(world, effect.deviceId, effect.mbps);
      break;

    // 章 6。感染フラグそのもの。通信量は setDeviceTraffic が別に受け持つ
    case 'setInfected':
      if (!deviceExists(world, effect.deviceId, effect.type)) break;
      outcome.changed = setInfected(world, effect.deviceId, effect.infected);
      break;

    default: {
      const exhaustive: never = effect;
      console.warn('[scenario] 未知の Effect です', exhaustive);
      break;
    }
  }

  return outcome;
}

/**
 * 干渉源の有効・無効を切り替える。
 *
 * 【なぜフラグ方式にしたか】
 * InterferenceSource には「いま動いているか」を持つ欄が無い（network.ts は変更しない）。
 * 直接指定にするなら strength を 0 にするしかないが、それでは
 * **元の強さが失われて元に戻せない**。章 4 は「プレイヤーが自分でレンジを回して
 * 因果を再現する」ことを狙うので、何度でも往復できる必要がある。
 *
 * 型には既に activeWhenFlag があり、フラグは GameState に入ってセーブもされる。
 * そこで、label が一致する干渉源すべてについて
 *   1. activeWhenFlag が無ければ `interference:<label>` を割り当て（世界側の変更）
 *   2. そのフラグを立てる／降ろす（進行状態側の変更）
 * とする。これで元の強さを壊さずに何度でも切り替えられる。
 */
function setInterferenceActive(
  state: GameState,
  world: NetworkWorld,
  label: string,
  active: boolean,
): boolean {
  const needle = label.trim();
  const matched: InterferenceSource[] = [];
  for (const deviceId of Object.keys(world.devices)) {
    for (const source of world.devices[deviceId].wireless?.interferenceSources ?? []) {
      if (source.label.trim() === needle) matched.push(source);
    }
  }
  if (matched.length === 0) {
    console.warn(`[scenario] 干渉源「${label}」が見つかりません`);
    return false;
  }

  let changed = false;
  for (const source of matched) {
    if (!source.activeWhenFlag) {
      source.activeWhenFlag = `interference:${needle}`;
      changed = true;
    }
    const flag = source.activeWhenFlag;
    if (state.flags[flag] !== active) {
      state.flags[flag] = active;
      changed = true;
    }
  }
  return changed;
}

/** Effect の配列をまとめて適用する */
export function applyEffects(
  effects: Effect[] | undefined,
  state: GameState,
  world: NetworkWorld,
): EffectOutcome {
  let result = emptyOutcome();
  for (const effect of effects ?? []) {
    result = mergeOutcome(result, applyEffect(effect, state, world));
  }
  return result;
}
